import React, { useRef, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ClientCard from './ClientCard';

const ClientsList = ({ clients, isLoading, hasNextPage, isFetchingNextPage, onFetchNextPage, onChat, onDetails }) => {
  const { t } = useTranslation();
  const observerTarget = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onFetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => { if (target) observer.unobserve(target); };
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (clients.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Typography variant="body1" color="text.secondary">
          {t('clients.noClientsFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      {clients.map((client) => (
        <ClientCard
          key={client.uuid}
          client={client}
          onChat={() => onChat(client)}
          onDetails={() => onDetails(client)}
        />
      ))}
      {hasNextPage && (
        <Box ref={observerTarget} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default ClientsList;
